import React from 'react'
import {
  useErrors,
  DefaultFormErrors,
  useAddCompany,
  useUpdateCompany,
  useBillingAddress,
  useGlobalLabels,
} from '@sentecacommerce-theme/lib'
import { AddressDTO } from '@sentecacommerce-theme/lib'
import { useForm } from 'react-hook-form'
import {
  BillingAddressForm,
  BillingAddressFormData,
} from '../BillingAddressForm'
import { EditAddress, addressDataIntitialState } from '../BillingAddressList'
import { Button } from '../../Button'
import styled from 'styled-components'
import { FormError } from '../../FormError'

type AddBillingAddressProps = {
  onSuccess: () => void
  onCancel: () => void
  hideCloseButton?: boolean
  editThisAddress?: EditAddress
  setEditThisAddress?: React.Dispatch<
    React.SetStateAction<{
      isEditing: boolean
      addressData: AddressDTO
    }>
  >
}

export const AddBillingAddress = ({
  onSuccess,
  onCancel,
  hideCloseButton,
  editThisAddress,
  setEditThisAddress,
}: AddBillingAddressProps) => {
  const [saveBillingAddress, { error }] = useAddCompany(onSuccess)
  const [updateBillingAddress, { error: updateError }] = useUpdateCompany(() =>
    setEditThisAddress?.({
      isEditing: false,
      addressData: addressDataIntitialState,
    })
  )
  const editDefaultData = useBillingAddress(editThisAddress?.addressData)
  const { errorMessages, cancel, save } = useGlobalLabels()
  const apiErrors = useErrors<DefaultFormErrors>(error, {
    default: errorMessages?.noResultsFound,
  })

  const onSubmit = (data: BillingAddressFormData) => {
    if (!editThisAddress?.isEditing) {
      saveBillingAddress({
        additionalAddressInfo: data?.companyAddress,
        countryCode: 'BG',
        company: {
          companyName: data?.company,
          vatId: data?.vatNumber,
          companyId: data?.uic,
          companyPerson: data?.companyPerson,
        },
        isDefaultBillingAddress: data?.isDefaultBillingAddress,
      })
    } else {
      updateBillingAddress({
        id: editThisAddress?.addressData?._id ?? '',
        dto: {
          additionalAddressInfo: data?.companyAddress,
          countryCode: 'BG',
          company: {
            companyName: data?.company,
            vatId: data?.vatNumber,
            companyId: data?.uic,
            companyPerson: data?.companyPerson,
          },
          isDefaultBillingAddress: data?.isDefaultBillingAddress,
        },
      })
    }
  }

  const { control, errors, handleSubmit } = useForm<BillingAddressFormData>({
    defaultValues: {
      uic: editDefaultData?.companyId || '',
      vatNumber: editDefaultData?.vatId || '',
      company: editDefaultData?.companyName || '',
      companyAddress: editDefaultData?.companyAddress || '',
      isDefaultBillingAddress:
        editDefaultData?.isDefaultBillingAddress || false,
      companyPerson: editDefaultData?.companyPerson || '',
    },
  })

  const buttonDisabled = Boolean(Object.keys(errors).length)

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BillingAddressForm control={control} errors={errors} />
      <SubmitWrapper>
        {!hideCloseButton && (
          <Button
            type="button"
            variant="green"
            bordered
            onClick={onCancel}
            borderRadius="small"
          >
            {cancel}
          </Button>
        )}

        <Button
          type="submit"
          disabled={buttonDisabled}
          className="ml-4"
          variant={buttonDisabled ? 'disabled' : 'green'}
          borderRadius="small"
        >
          {save}
        </Button>
        <FormError errors={apiErrors} fieldName="default" />
      </SubmitWrapper>
    </form>
  )
}

const SubmitWrapper = styled.div.attrs({
  className: 'mt-5 pt-5',
})`
  border-top: 1px solid ${({ theme }) => theme.palette.lightgrey};
  display: flex;
  justify-content: flex-end;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    & button {
      width: 21% !important;
    }
  }
`
